import { Text } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { MoneyCellRenderer } from "~/pages/Billing/components/MoneyCellRenderer";
import {
  GRID_COLUMN_FILTER_DEBOUNCE_MS,
  PRODUCT_LINE_FILTER_VALUES,
} from "~/pages/Billing/constants";
import { NOT_ASSIGNED_LABEL, NOT_ASSIGNED_RAW_ID } from "../constants";
import { CaseTypeDisplay } from "../types";
import { notAssignedLabel, proposedChangesCellClass } from "./styles_vanilla.css";
import type { CaseEntityRow } from "./types";
import type {
  CellClassParams,
  ColDef,
  ColGroupDef,
  ICellRendererParams,
} from "ag-grid-community";

export const billingGroupCellRenderer = (
  data: CaseEntityRow | undefined,
): JSX.Element | string | null => {
  if (!data) {
    return null;
  }
  if (data.type !== "billingGroup") {
    return null;
  }
  const name = data.billingGroupName ?? "";
  if (name === NOT_ASSIGNED_LABEL) {
    return <span className={notAssignedLabel}>{name}</span>;
  }
  return name;
};

export const entityValueGetter = (data: CaseEntityRow | undefined): string | null =>
  data?.type === "entity" ? data.entity : null;

export const billableFaEumUsdValueGetter = (data: CaseEntityRow | undefined): number | null => {
  if (data?.type === "billingGroup") {
    return data.billableFaEumUsd;
  }
  return data?.billableFaEumUsd ?? null;
};

export const jsqProductLineValueGetter = (data: CaseEntityRow | undefined): string | null => {
  if (data?.type !== "entity" || !data.entityData) {
    return null;
  }
  return data.entityData.jsqProductLine ?? null;
};

export const firstCapitalCallDateValueGetter = (
  data: CaseEntityRow | undefined,
): string | null => {
  if (data?.type !== "entity" || !data.entityData) {
    return null;
  }
  return data.entityData.firstCapitalCallDate ?? null;
};

export const safeCaseIdValueGetter = (data: CaseEntityRow | undefined): string | null => {
  if (data?.type !== "entity" || !data.entityData) {
    return null;
  }
  return data.entityData.safeCaseId ?? null;
};

export const faEntityNumberValueGetter = (data: CaseEntityRow | undefined): number | null => {
  if (data?.type !== "entity" || !data.entityData) {
    return null;
  }
  return data.entityData.faEntityNumber ?? null;
};

export const proposedChangesValueGetter = (
  data: CaseEntityRow | undefined,
  familyProposedChangeCounts?: Map<string, number>,
): number | null => {
  if (!data) {
    return null;
  }
  if (data.type === "billingGroup") {
    // Lookup by billingGroupName (not billingGroupId) because the counts map
    // is keyed by familyDisplayName from the billing DB, which matches
    // billingGroupName from the DW (but billingGroupId is a different ID space).
    if (!data.billingGroupName) {
      return null;
    }
    return familyProposedChangeCounts?.get(data.billingGroupName) ?? null;
  }
  if (data.type === "entity") {
    return data.proposedChanges ?? 0;
  }
  return null;
};

export const proposedChangesCellRenderer = (
  data: CaseEntityRow | undefined,
  onEntityProposedChangeClick: (entityId: number, entityName: string | null) => void,
  onFamilyProposedChangeClick: (familyName: string | null) => void,
  familyProposedChangeCounts?: Map<string, number>,
): JSX.Element | null => {
  if (!data) {
    return null;
  }

  if (data.type === "billingGroup") {
    // Lookup by billingGroupName (keyed by familyDisplayName from billing DB)
    const count =
      data.billingGroupName && familyProposedChangeCounts?.get(data.billingGroupName);

    if (!count) {
      return null;
    }
    return (
      <Text
        variant="regular-md"
        color={Colors.Informative600}
        data-testid={`proposed-changes-family-count-${data.billingGroupName}`}
      >
        {count}
      </Text>
    );
  }

  if (data.type === "entity") {
    const entityId = data.entityId;
    const count = data.proposedChanges;
    if (!count || typeof entityId !== "number") {
      return <Text variant="regular-md">None</Text>;
    }
    return (
      <Text
        variant="regular-md"
        color={Colors.Informative600}
        data-testid={`proposed-changes-count-${entityId}`}
      >
        {count}
      </Text>
    );
  }

  return null;
};

export type CaseEntitiesColumnDefsParams = {
  onEntityProposedChangeClick: (entityId: number, entityName: string | null) => void;
  onFamilyProposedChangeClick: (familyName: string | null) => void;
  familyProposedChangeCountsRef: { current: Map<string, number> | undefined };
  isSafeCaseIdEnabled?: boolean;
};

export const getCaseEntitiesColumnDefs = ({
  onEntityProposedChangeClick,
  onFamilyProposedChangeClick,
  familyProposedChangeCountsRef,
  isSafeCaseIdEnabled = false,
}: CaseEntitiesColumnDefsParams): (ColDef<CaseEntityRow> | ColGroupDef<CaseEntityRow>)[] => [
  {
    field: "billingGroupId",
    headerName: "Billing group",
    rowGroup: true,
    hide: true,
    suppressColumnsToolPanel: true,
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
    },
  },
  {
    colId: "entity",
    headerName: "Entity",
    minWidth: 200,
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
    },
    menuTabs: ["generalMenuTab", "filterMenuTab"],
    valueGetter: (params) => entityValueGetter(params.data),
  },
  {
    colId: "billableFaEumUsd",
    headerName: "Billable FA EUM (USD)",
    minWidth: 140,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: { rounded: true },
    cellStyle: { textAlign: "right" },
    valueGetter: (params) => billableFaEumUsdValueGetter(params.data),
    aggFunc: "sum",
  },
  {
    field: "entityId",
    headerName: "Entity ID",
    minWidth: 110,
    cellStyle: { textAlign: "right" },
  },
  {
    colId: "jsqProductLine",
    headerName: "JSQ product line",
    minWidth: 140,
    filter: "agSetColumnFilter",
    filterParams: {
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
      values: PRODUCT_LINE_FILTER_VALUES,
    },
    menuTabs: ["generalMenuTab", "filterMenuTab"],
    valueGetter: (params) => jsqProductLineValueGetter(params.data),
  },
  {
    colId: "firstCapitalCallDate",
    headerName: CaseTypeDisplay.FirstCapitalCallDate,
    minWidth: 140,
    valueGetter: (params) => firstCapitalCallDateValueGetter(params.data),
    hide: true,
  },
  ...(isSafeCaseIdEnabled
    ? [
        {
          colId: "safeCaseId",
          headerName: "Safe Case ID",
          minWidth: 150,
          valueGetter: (params: { data: CaseEntityRow | undefined }) =>
            safeCaseIdValueGetter(params.data),
          hide: false,
        },
      ]
    : []),
  {
    colId: "faEntityNumber",
    headerName: "FA Entity Number",
    minWidth: 150,
    valueGetter: (params) => faEntityNumberValueGetter(params.data),
    cellStyle: { textAlign: "right" },
    hide: false,
  },
  {
    colId: "proposedChanges",
    headerName: "Proposed changes",
    filter: false,
    minWidth: 140,
    valueGetter: (params) =>
      proposedChangesValueGetter(params.data, familyProposedChangeCountsRef.current),
    cellRenderer: (params: ICellRendererParams<CaseEntityRow>) =>
      proposedChangesCellRenderer(
        params.data,
        onEntityProposedChangeClick,
        onFamilyProposedChangeClick,
        familyProposedChangeCountsRef.current,
      ),
    cellStyle: { alignContent: "center" },
    cellClass: (params: CellClassParams<CaseEntityRow>) => {
      const count = proposedChangesValueGetter(
        params.data,
        familyProposedChangeCountsRef.current,
      );
      return count ? proposedChangesCellClass : undefined;
    },
    onCellClicked: (params) => {
      const data = params.data;
      if (!data) return;
      if (data.type === "billingGroup" && data.billingGroupName) {
        const count = familyProposedChangeCountsRef.current?.get(data.billingGroupName);
        if (count) {
          onFamilyProposedChangeClick(data.billingGroupName);
        }
      } else if (data.type === "entity" && data.proposedChanges && data.entityId) {
        onEntityProposedChangeClick(data.entityId, data.entity ?? null);
      }
    },
  },
];

export const getCaseEntitiesAutoGroupColumnDef = (isMaker: boolean): ColDef<CaseEntityRow> => ({
  headerName: "Group",
  minWidth: 180,
  filter: "agGroupColumnFilter",
  menuTabs: ["generalMenuTab", "filterMenuTab"],
  cellRendererParams: {
    innerRenderer: (params: { data: CaseEntityRow | undefined }) =>
      billingGroupCellRenderer(params.data),
    suppressCount: true,
  },
  checkboxSelection: isMaker
    ? (params) => {
        if (!params.node.group) {
          return true;
        }
        const key = params.node.key;
        return key !== NOT_ASSIGNED_LABEL && key !== NOT_ASSIGNED_RAW_ID;
      }
    : false,
  showDisabledCheckboxes: isMaker,
  pinned: "left",
});
